import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import "./OrderChat.scss";
import {
  theme,
  Drawer,
  Typography,
  Input,
  Button,
  Spin,
  notification,
} from "antd";
import {
  CloseOutlined,
  SendOutlined,
  MessageOutlined,
  AudioOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import useWindowWidth from "../../hooks/useWindowWidth";
import useVoiceInput from "../../hooks/useVoiceInput";
import {
  getOrderMessages,
  sendOrderMessage,
  markOrderChatRead,
} from "../../features/orderChat/orderChatActions";
import {
  closeChat,
  resetOrderChatError,
} from "../../features/orderChat/orderChatSlice";
import Utils from "../../utils";

const { TextArea } = Input;

export const MESSAGE_MAX_LENGTH = 1500;
export const EMPTY_THREAD_TITLE = "No messages yet";
export const EMPTY_THREAD_HINT = "Ask the store anything about this order.";

// Time under each bubble, in the user's own locale.
const formatTime = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// The chat drawer for the order whose id is `orderChat.openOrderId`. Mounted
// once on the Orders page; the cards open it through `openChat`.
export default function OrderChat() {
  const { useToken } = theme;
  const { token } = useToken();
  const dispatch = useDispatch();
  const { isMobile } = useWindowWidth();
  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (type, title, text) => {
      api[type]({
        title: title,
        description: Utils.getErrorText(text),
        placement: "top",
      });
    },
    [api]
  );

  const { openOrderId, openOrderNumber, threads, unread, sending, sendError } =
    useSelector((state) => state?.orderChat) || {};
  const shop = useSelector((state) => state?.shopDetails?.shop);
  // Staff bubbles carry the store's name, the same one the header shows —
  // never a person's name.
  const storeName =
    shop?.name && shop?.is_name_visible_on_shop ? shop?.name : "Store";

  const isOpen = Boolean(openOrderId);
  const thread = isOpen ? threads?.[openOrderId] : null;
  const threadItems = thread?.items;
  const items = useMemo(() => threadItems || [], [threadItems]);
  const [draft, setDraft] = useState("");
  const listRef = useRef(null);
  const composerRef = useRef(null);
  // Newest message id already reported read for the open thread, so a reply
  // that lands while the drawer is open is marked exactly once.
  const lastReadIdRef = useRef(null);
  const initialMarkDoneRef = useRef(false);

  // On open: fresh draft, reload the thread (the socket may have missed
  // something while the tab was asleep).
  useEffect(() => {
    if (!openOrderId) {
      return;
    }
    setDraft("");
    lastReadIdRef.current = null;
    initialMarkDoneRef.current = false;
    dispatch(getOrderMessages({ orderId: openOrderId }));
  }, [openOrderId, dispatch]);

  // Mark read: once with the newest rendered id after the thread is loaded,
  // then again for every staff reply that arrives while the drawer is open.
  useEffect(() => {
    if (!openOrderId || !thread?.loaded) {
      return;
    }
    const newest = items?.length ? items[items.length - 1] : null;
    if (!initialMarkDoneRef.current) {
      initialMarkDoneRef.current = true;
      if (newest?.id) {
        lastReadIdRef.current = newest.id;
        dispatch(
          markOrderChatRead({ orderId: openOrderId, upToId: newest.id })
        );
      } else if ((unread?.[openOrderId] || 0) > 0) {
        // Badge says unread but the thread is empty — clear everything.
        dispatch(markOrderChatRead({ orderId: openOrderId }));
      }
      return;
    }
    if (
      newest?.id &&
      newest?.direction === "OUTBOUND" &&
      (lastReadIdRef.current === null || newest.id > lastReadIdRef.current)
    ) {
      lastReadIdRef.current = newest.id;
      dispatch(markOrderChatRead({ orderId: openOrderId, upToId: newest.id }));
    }
  }, [openOrderId, thread?.loaded, items, unread, dispatch]);

  // Keep the newest bubble in view.
  useEffect(() => {
    const el = listRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [items?.length, isOpen, thread?.loading]);

  // Load and send errors surface as notifications, like every other page.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const error = sendError || thread?.error;
    if (error) {
      openNotificationWithIcon("error", "Chat", error);
      dispatch(resetOrderChatError());
    }
  }, [isOpen, sendError, thread?.error, openNotificationWithIcon, dispatch]);

  // Voice input, the same way the staff app's order chat does it: the
  // transcript is appended to whatever was typed before recording started,
  // and a late result after a send must not refill the just-cleared field.
  const baseTextRef = useRef("");
  const ignoreVoiceResultRef = useRef(false);
  const handleVoiceResult = useCallback((transcript) => {
    if (ignoreVoiceResultRef.current) return;
    const base = baseTextRef.current;
    const next = base ? `${base} ${transcript}` : transcript;
    setDraft(next?.slice(0, MESSAGE_MAX_LENGTH));
  }, []);
  const {
    isSupported: isVoiceSupported,
    isRecording,
    start: startVoice,
    stop: stopVoice,
  } = useVoiceInput({
    onResult: handleVoiceResult,
    onError: (msg) => openNotificationWithIcon("error", "Voice input", msg),
  });

  const toggleVoice = useCallback(() => {
    if (isRecording) {
      stopVoice();
    } else {
      baseTextRef.current = draft;
      ignoreVoiceResultRef.current = false;
      startVoice();
    }
    // Back to the field so Enter sends instead of re-toggling the mic.
    composerRef.current?.focus?.();
  }, [isRecording, draft, startVoice, stopVoice]);

  // Closing the drawer must not leave the mic open.
  useEffect(() => {
    if (!isOpen) stopVoice();
  }, [isOpen, stopVoice]);

  const handleSend = () => {
    const text = draft?.trim();
    if (!text || sending || !openOrderId) {
      return;
    }
    if (isRecording) {
      ignoreVoiceResultRef.current = true;
      stopVoice();
    }
    dispatch(sendOrderMessage({ orderId: openOrderId, message: text })).then(
      (res) => {
        if (res?.meta?.requestStatus === "fulfilled") {
          setDraft("");
        }
      }
    );
  };

  const canSend = Boolean(draft?.trim()) && !sending;

  return (
    <Drawer
      className="orderChat-cont"
      open={isOpen}
      destroyOnHidden={true}
      placement="right"
      // Wide like the staff app's order drawer; full width on phones.
      size={isMobile ? "100%" : 560}
      style={{
        backgroundColor: token?.Drawer?.colorBgDrawer,
      }}
      styles={{
        // Same height and green rule as the app header, so the drawer's
        // divider continues the header's line across the page.
        // antd gives the header `flex: 0`, which would shrink it back to its
        // content height, so the flex basis is pinned too.
        header: {
          flex: "0 0 auto",
          height: isMobile ? 56 : 80,
          minHeight: isMobile ? 56 : 80,
          boxSizing: "border-box",
          borderBottom: `1px solid ${token?.colorPrimary}`,
        },
        body: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      title={
        <Typography.Text
          className="orderChat-cont__title"
          style={{
            color: token?.Typography?.mainColorText,
          }}>
          Order {openOrderNumber}
        </Typography.Text>
      }
      closeIcon={
        <CloseOutlined
          style={{
            color: token?.icons?.whiteIcon,
            fontSize: "20px",
          }}
        />
      }
      onClose={() => {
        dispatch(closeChat());
      }}>
      {contextHolder}
      <div className="orderChat-cont__body">
        <div
          className="orderChat-cont__panel"
          style={{
            backgroundColor: token?.Card?.colorBgCard,
          }}>
          <div
            className="orderChat-cont__panel__list"
            ref={listRef}
            role="log"
            aria-live="polite"
            aria-label="Messages">
            {thread?.loading && !items?.length ? (
              <div className="orderChat-cont__panel__spinWrapper">
                <Spin size="large" />
              </div>
            ) : items?.length ? (
              items.map((m) => {
                const isMine = m?.direction === "INBOUND";
                return (
                  <div
                    key={m?.id}
                    className={classNames("orderChat-cont__bubble", {
                      "orderChat-cont__bubble--in": isMine,
                      "orderChat-cont__bubble--out": !isMine,
                    })}
                    style={{
                      backgroundColor: isMine
                        ? token?.colorFillTertiary
                        : token?.Layout?.contentBg,
                      borderColor: isMine
                        ? token?.colorPrimary
                        : "color-mix(in srgb, var(--text-main, #fff) 10%, transparent)",
                    }}>
                    <Typography.Text className="orderChat-cont__bubble__who">
                      {isMine ? "You" : storeName}
                    </Typography.Text>
                    <Typography.Text
                      className="orderChat-cont__bubble__text"
                      style={{
                        color: token?.Typography?.mainColorText,
                      }}>
                      {m?.message}
                    </Typography.Text>
                    <Typography.Text className="orderChat-cont__bubble__time">
                      {formatTime(m?.created_at)}
                    </Typography.Text>
                  </div>
                );
              })
            ) : thread?.loaded ? (
              <div className="orderChat-cont__panel__empty">
                <MessageOutlined className="orderChat-cont__panel__empty__icon" />
                <Typography.Text
                  className="orderChat-cont__panel__empty__title"
                  style={{
                    color: token?.Typography?.mainColorText,
                  }}>
                  {EMPTY_THREAD_TITLE}
                </Typography.Text>
                <Typography.Text className="orderChat-cont__panel__empty__hint">
                  {EMPTY_THREAD_HINT}
                </Typography.Text>
              </div>
            ) : null}
          </div>
        </div>
        <div
          className="orderChat-cont__composer"
          style={{
            backgroundColor: token?.Card?.colorBgCard,
          }}>
          <TextArea
            ref={composerRef}
            className="orderChat-cont__composer__textArea"
            variant="borderless"
            placeholder="Write a message..."
            aria-label="Message"
            autoSize={{ minRows: 2, maxRows: 6 }}
            maxLength={MESSAGE_MAX_LENGTH}
            value={draft}
            disabled={sending}
            onChange={(e) => {
              setDraft(e?.target?.value);
            }}
            // Enter sends, Shift+Enter makes a new line.
            onPressEnter={(e) => {
              if (!e?.shiftKey) {
                e?.preventDefault?.();
                handleSend();
              }
            }}
          />
          <div className="orderChat-cont__composer__footer">
            <Typography.Text className="orderChat-cont__composer__footer__hint">
              Enter to send · Shift+Enter for a new line
            </Typography.Text>
            <div className="orderChat-cont__composer__footer__right">
              <span className="orderChat-cont__composer__footer__counter">
                {draft?.length || 0}/{MESSAGE_MAX_LENGTH}
              </span>
              {isVoiceSupported && (
                <Button
                  type="text"
                  shape="circle"
                  className={classNames(
                    "orderChat-cont__composer__footer__mic",
                    {
                      "orderChat-cont__composer__footer__mic--recording":
                        isRecording,
                    }
                  )}
                  aria-label={
                    isRecording ? "Stop voice input" : "Start voice input"
                  }
                  icon={<AudioOutlined />}
                  disabled={sending}
                  onClick={toggleVoice}
                />
              )}
              <Button
                type="primary"
                shape="circle"
                aria-label="Send"
                icon={<SendOutlined />}
                loading={sending}
                disabled={!canSend}
                onClick={handleSend}
              />
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
